import { $, bindClick } from "../lib/dom.js";
import { escHtml } from "../lib/hints.js";
import { api } from "../api.js";
import { state } from "../state.js";

const GAODE_TILE =
  "https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=8&x={x}&y={y}&z={z}";
const REGION_API = "/api/v1/geo/regions";

let currentStoreId = "";
let mapState = { kind: null, map: null, marker: null };
let leafletReady = null;
let amapReady = null;
let regeoBusy = false;
let provincesLoaded = false;
let cascadeToken = 0;

function notify(text, ok) {
  const el = $("key-banner");
  if (!el) return;
  el.textContent = text;
  el.classList.toggle("hidden", !text);
  el.classList.toggle("ok", !!ok);
  // 表单在页面下半截，提示条在顶栏下方：不拉回视口就等于「点了没反应」。
  if (text) {
    try {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } catch {
      /* 老浏览器忽略 */
    }
  }
}

function readNum(id) {
  const el = $(id);
  if (!el) return null;
  const v = parseFloat(String(el.value || "").trim());
  return Number.isFinite(v) ? v : null;
}

async function loadRegionOptions(sel, parentCode, placeholder) {
  if (!sel) return [];
  sel.innerHTML = `<option value="">${escHtml(placeholder)}</option>`;
  sel.disabled = true;
  const qs = parentCode ? `?parent_code=${encodeURIComponent(parentCode)}` : "";
  const { ok, data } = await api("GET", `${REGION_API}${qs}`);
  const items = ok && data ? data.items || [] : [];
  items.forEach((it) => {
    const o = document.createElement("option");
    o.value = it.region_code;
    o.textContent = it.name;
    sel.appendChild(o);
  });
  sel.disabled = false;
  return items;
}

function resetRegionSelect(sel, placeholder) {
  if (!sel) return;
  sel.innerHTML = `<option value="">${escHtml(placeholder)}</option>`;
  sel.disabled = true;
}

async function loadProvinces(force) {
  if (provincesLoaded && !force) return;
  await loadRegionOptions($("os-region-province"), "", "请选择省份");
  provincesLoaded = true;
}

/** 级联选完把地图转到该区划中心；**不落 pin、不改 lng/lat** —— 坐标必须由用户点图确认。 */
async function recenterToRegion(code) {
  if (!code) return;
  if (!mapState.map) {
    try {
      await ensureMap();
    } catch {
      return;
    }
  }
  const { ok, data } = await api("GET", `${REGION_API}/${encodeURIComponent(code)}/center`);
  if (!ok || !data || !data.ok) return;
  const lng = Number(data.lng);
  const lat = Number(data.lat);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
  if (mapState.kind === "leaflet" && mapState.map) {
    mapState.map.setView([lat, lng], 12);
  } else if (mapState.kind === "amap" && mapState.map) {
    if (typeof mapState.map.setZoomAndCenter === "function") {
      mapState.map.setZoomAndCenter(12, [lng, lat]);
    } else {
      mapState.map.setCenter([lng, lat]);
    }
  }
}

async function onProvinceChange() {
  const province = ($("os-region-province") && $("os-region-province").value) || "";
  const city = $("os-region-city");
  resetRegionSelect($("os-region-district"), "请选择区县");
  if (!province) {
    resetRegionSelect(city, "请选择城市");
    return;
  }
  await loadRegionOptions(city, province, "全部城市");
  await recenterToRegion(province);
}

async function onCityChange() {
  const city = ($("os-region-city") && $("os-region-city").value) || "";
  const district = $("os-region-district");
  if (!city) {
    resetRegionSelect(district, "请选择区县");
    return;
  }
  await loadRegionOptions(district, city, "全部区县");
  await recenterToRegion(city);
}

function onDistrictChange() {
  const district = ($("os-region-district") && $("os-region-district").value) || "";
  if (district) recenterToRegion(district);
}

function setSelectValue(sel, value) {
  if (!sel || !value) return false;
  sel.value = value;
  return sel.value === value;
}

/** 回显已有门店的省 / 市 / 区县：门店表只存 L2 city_code，能回填到市级，区县留空。 */
async function prefillRegionCascade(regionCode) {
  const prov = $("os-region-province");
  const city = $("os-region-city");
  const district = $("os-region-district");
  if (!prov || !city || !district) return;
  if (!regionCode) return;
  const token = ++cascadeToken;
  const { ok, data } = await api("GET", `${REGION_API}/${encodeURIComponent(regionCode)}`);
  if (token !== cascadeToken || !ok || !data || !data.region) return;
  const chain = (data.ancestors || []).slice().reverse();
  chain.push(data.region);
  const byLevel = {};
  chain.forEach((n) => {
    byLevel[String(n.level)] = n.region_code;
  });
  const provinceCode = byLevel["1"] || "";
  const cityCode = byLevel["2"] || "";
  const districtCode = byLevel["3"] || "";
  if (!provinceCode || !setSelectValue(prov, provinceCode)) return;
  await loadRegionOptions(city, provinceCode, "全部城市");
  if (token !== cascadeToken) return;
  resetRegionSelect(district, "请选择区县");
  if (cityCode && setSelectValue(city, cityCode)) {
    await loadRegionOptions(district, cityCode, "全部区县");
    if (token !== cascadeToken) return;
    setSelectValue(district, districtCode);
  }
}

function renderStoreMeta(store) {
  const meta = $("os-store-meta");
  if (!meta) return;
  if (!store || !store.store_id) {
    meta.classList.add("hidden");
    meta.textContent = "";
    return;
  }
  meta.classList.remove("hidden");
  const code = store.shop_code ? ` · shop_code <code>${escHtml(store.shop_code)}</code>` : "";
  meta.innerHTML = `store_id <code>${escHtml(store.store_id)}</code>${code}`;
}

function renderGeoSummary() {
  const el = $("os-store-geo-summary");
  if (!el) return;
  const lng = readNum("os-store-lng");
  const lat = readNum("os-store-lat");
  const addr = ($("os-store-address") && $("os-store-address").value.trim()) || "";
  const city =
    ($("os-store-city-name") && $("os-store-city-name").value.trim()) ||
    ($("os-store-city-code") && $("os-store-city-code").value.trim()) ||
    "";
  if (lng == null || lat == null) {
    el.innerHTML = "<p class='mc-desc'>请在地图上点击门店位置，地址与城市将自动识别。</p>";
    return;
  }
  el.innerHTML = `<div class="os-geo-card">
    <div><strong>地址</strong> ${escHtml(addr || (regeoBusy ? "识别中…" : "—"))}</div>
    <div class="mc-desc">城市：${escHtml(city || "—")} · 坐标 ${lng.toFixed(6)}, ${lat.toFixed(6)}</div>
  </div>`;
}

function destroyMap() {
  if (mapState.kind === "leaflet" && mapState.map) {
    mapState.map.remove();
  } else if (mapState.kind === "amap" && mapState.map) {
    mapState.map.destroy();
  }
  mapState = { kind: null, map: null, marker: null };
}

function refreshMapLayout() {
  if (mapState.kind === "leaflet" && mapState.map) {
    requestAnimationFrame(() => {
      if (mapState.map) mapState.map.invalidateSize();
    });
  } else if (mapState.kind === "amap" && mapState.map) {
    requestAnimationFrame(() => {
      if (mapState.map) mapState.map.resize();
    });
  }
}

function clearStoreForm() {
  currentStoreId = "";
  [
    ["os-store-code", ""],
    ["os-store-name", ""],
    ["os-store-contact", ""],
    ["os-store-lng", ""],
    ["os-store-lat", ""],
    ["os-store-address", ""],
    ["os-store-city-code", ""],
    ["os-store-city-name", ""],
  ].forEach(([id, val]) => {
    if ($(id)) $(id).value = val;
  });
  if ($("os-store-status")) $("os-store-status").value = "pending";
  cascadeToken += 1;
  if ($("os-region-province")) $("os-region-province").value = "";
  resetRegionSelect($("os-region-city"), "请选择城市");
  resetRegionSelect($("os-region-district"), "请选择区县");
  renderStoreMeta(null);
  renderGeoSummary();
  if (mapState.marker) {
    if (mapState.kind === "leaflet" && mapState.map) {
      mapState.map.removeLayer(mapState.marker);
    } else if (mapState.kind === "amap") {
      mapState.marker.setMap(null);
    }
    mapState.marker = null;
  }
}

function writeStoreFields(store) {
  if (!store) {
    clearStoreForm();
    return;
  }
  currentStoreId = String(store.store_id || "").trim();
  if ($("os-store-code")) $("os-store-code").value = store.shop_code || "";
  if ($("os-store-name")) $("os-store-name").value = store.shop_name || "";
  if ($("os-store-contact")) $("os-store-contact").value = store.cs_contact || "";
  if ($("os-store-lng")) $("os-store-lng").value = store.lng != null ? store.lng : "";
  if ($("os-store-lat")) $("os-store-lat").value = store.lat != null ? store.lat : "";
  if ($("os-store-address")) $("os-store-address").value = store.address_text || "";
  if ($("os-store-city-code")) $("os-store-city-code").value = store.city_code || "";
  if ($("os-store-city-name")) $("os-store-city-name").value = store.city_name || "";
  if ($("os-store-status")) $("os-store-status").value = store.status || "pending";
  renderStoreMeta(store);
  renderGeoSummary();
  prefillRegionCascade(store.city_code);
  const lng = readNum("os-store-lng");
  const lat = readNum("os-store-lat");
  if (lng != null && lat != null && mapState.map) setPin(lng, lat, false);
}

async function loadAmapConfig() {
  const base = ($("base-url") && $("base-url").value.trim()) || window.location.origin;
  const res = await fetch(`${base.replace(/\/$/, "")}/api/v1/demo/config`);
  const data = await res.json().catch(() => ({}));
  return {
    jsKey: String(data.amap_js_key || "").trim(),
    security: String(data.amap_security_js_code || "").trim(),
  };
}

function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletReady) return leafletReady;
  leafletReady = new Promise((resolve, reject) => {
    if (!document.querySelector("link[data-os-leaflet]")) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "/static/vendor/leaflet/leaflet.css";
      link.dataset.osLeaflet = "1";
      document.head.appendChild(link);
    }
    const s = document.createElement("script");
    s.src = "/static/vendor/leaflet/leaflet.js";
    s.onload = () => resolve(window.L);
    s.onerror = () => reject(new Error("Leaflet 地图库加载失败"));
    document.head.appendChild(s);
  });
  return leafletReady;
}

function loadAmapScript(jsKey, security) {
  if (window.AMap) return Promise.resolve(window.AMap);
  if (amapReady) return amapReady;
  amapReady = new Promise((resolve, reject) => {
    if (security) window._AMapSecurityConfig = { securityJsCode: security };
    const s = document.createElement("script");
    s.src = `https://webapi.amap.com/maps?v=2.0&key=${encodeURIComponent(jsKey)}`;
    s.onload = () => resolve(window.AMap);
    s.onerror = () => reject(new Error("高德地图脚本加载失败"));
    document.head.appendChild(s);
  });
  return amapReady;
}

async function reverseGeocodeAt(lng, lat) {
  regeoBusy = true;
  renderGeoSummary();
  const { ok, data } = await api("POST", "/api/v1/geo/regeo", { body: { lng, lat } });
  regeoBusy = false;
  if (ok && data && data.ok) {
    if ($("os-store-address")) {
      $("os-store-address").value = data.formatted_address || "";
    }
    if ($("os-store-city-code")) {
      $("os-store-city-code").value = data.city_code || data.adcode || "";
    }
    if ($("os-store-city-name")) {
      $("os-store-city-name").value = data.city_name || "";
    }
  } else if (!ok || (data && !data.ok)) {
    const msg =
      (data && (data.info || data.message)) ||
      (data && data.error && data.error.message) ||
      "逆地理编码失败，坐标仍可保存";
    notify(msg, false);
  }
  renderGeoSummary();
}

function setPin(lng, lat, runRegeo = true) {
  if ($("os-store-lng")) $("os-store-lng").value = lng.toFixed(6);
  if ($("os-store-lat")) $("os-store-lat").value = lat.toFixed(6);
  if (mapState.kind === "leaflet" && mapState.map && window.L) {
    const pos = [lat, lng];
    if (mapState.marker) mapState.marker.setLatLng(pos);
    else {
      mapState.marker = window.L.marker(pos, { draggable: true }).addTo(mapState.map);
      mapState.marker.on("dragend", () => {
        const p = mapState.marker.getLatLng();
        setPin(p.lng, p.lat, true);
      });
    }
    mapState.map.setView(pos, Math.max(mapState.map.getZoom(), 14));
  } else if (mapState.kind === "amap" && mapState.map && window.AMap) {
    if (mapState.marker) mapState.marker.setPosition([lng, lat]);
    else mapState.marker = new window.AMap.Marker({ position: [lng, lat], map: mapState.map });
    mapState.map.setCenter([lng, lat]);
  }
  if (runRegeo) reverseGeocodeAt(lng, lat);
  else renderGeoSummary();
}

async function ensureLeafletMap() {
  const wrap = $("os-store-map");
  if (!wrap) return false;
  const L = await loadLeaflet();
  const lng = readNum("os-store-lng") || 120.15507;
  const lat = readNum("os-store-lat") || 30.27415;
  if (mapState.kind !== "leaflet") {
    destroyMap();
    mapState.kind = "leaflet";
    mapState.map = L.map(wrap, { center: [lat, lng], zoom: 14, zoomControl: true });
    L.tileLayer(GAODE_TILE, {
      subdomains: ["1", "2", "3", "4"],
      maxZoom: 18,
      attribution: "高德地图",
    }).addTo(mapState.map);
    mapState.map.on("click", (ev) => {
      if (ev && ev.latlng) setPin(ev.latlng.lng, ev.latlng.lat, true);
    });
  }
  refreshMapLayout();
  const hasPin = readNum("os-store-lng") != null && readNum("os-store-lat") != null;
  if (hasPin) setPin(readNum("os-store-lng"), readNum("os-store-lat"), false);
  else mapState.map.setView([lat, lng], 14);
  return true;
}

async function ensureAmapMap(cfg) {
  const wrap = $("os-store-map");
  if (!wrap) return false;
  await loadAmapScript(cfg.jsKey, cfg.security);
  const lng = readNum("os-store-lng") || 120.15507;
  const lat = readNum("os-store-lat") || 30.27415;
  if (mapState.kind !== "amap") {
    destroyMap();
    wrap.innerHTML = "";
    mapState.kind = "amap";
    mapState.map = new window.AMap.Map(wrap, { zoom: 14, center: [lng, lat] });
    mapState.map.on("click", (ev) => {
      const p = ev.lnglat;
      if (p) setPin(p.lng, p.lat, true);
    });
  }
  refreshMapLayout();
  const hasPin = readNum("os-store-lng") != null && readNum("os-store-lat") != null;
  if (hasPin) setPin(readNum("os-store-lng"), readNum("os-store-lat"), false);
  else mapState.map.setCenter([lng, lat]);
  return true;
}

async function ensureMap() {
  const wrap = $("os-store-map");
  if (!wrap) return false;
  const cfg = await loadAmapConfig();
  try {
    if (cfg.jsKey) return await ensureAmapMap(cfg);
    return await ensureLeafletMap();
  } catch (err) {
    notify((err && err.message) || "地图初始化失败", false);
    return false;
  }
}

function statusBadge(status) {
  const st = String(status || "pending").toLowerCase();
  const map = {
    pending: { label: "待激活", cls: "pending" },
    active: { label: "已启用", cls: "active" },
    disabled: { label: "已停用", cls: "disabled" },
  };
  const hit = map[st] || { label: st, cls: "pending" };
  return `<span class="os-store-status os-store-status--${hit.cls}">${escHtml(hit.label)}</span>`;
}

function truncateText(text, max) {
  const s = String(text || "").trim();
  if (!s) return "—";
  if (s.length <= max) return escHtml(s);
  return `<span title="${escHtml(s)}">${escHtml(s.slice(0, max))}…</span>`;
}

function renderStoreList(stores) {
  const box = $("os-store-list");
  const countEl = $("os-store-count");
  if (countEl) {
    countEl.textContent = stores.length ? `（${stores.length}）` : "";
  }
  if (!box) return;
  if (!stores.length) {
    box.innerHTML = "<p class='os-store-empty'>尚无门店，点击「登记新门店」并在地图选点后保存。</p>";
    return;
  }
  const rows = stores
    .map((st) => {
      const active = st.store_id === currentStoreId ? " os-store-row-active" : "";
      const name = escHtml(st.shop_name || "—");
      const contact = escHtml(st.cs_contact || "—");
      const city = escHtml(st.city_name || st.city_code || "—");
      const addr = truncateText(st.address_text, 28);
      const code = st.shop_code ? `<code class="os-store-code">${escHtml(st.shop_code)}</code>` : "—";
      return `<tr class="os-store-row${active}" data-store-id="${escHtml(st.store_id)}">
        <td class="os-store-col-name">${name}</td>
        <td class="os-store-col-contact">${contact}</td>
        <td class="os-store-col-city">${city}</td>
        <td class="os-store-col-addr">${addr}</td>
        <td class="os-store-col-status">${statusBadge(st.status)}</td>
        <td class="os-store-col-code">${code}</td>
      </tr>`;
    })
    .join("");
  box.innerHTML = `<div class="table-scroll os-store-table-scroll"><table class="data os-store-table">
    <thead><tr>
      <th>店名</th><th>联系电话</th><th>城市</th><th>地址</th><th>状态</th><th>门店编码</th>
    </tr></thead>
    <tbody>${rows}</tbody></table></div>`;
  box.querySelectorAll(".os-store-row").forEach((row) => {
    row.addEventListener("click", async () => {
      const sid = row.getAttribute("data-store-id");
      const hit = stores.find((s) => s.store_id === sid);
      writeStoreFields(hit);
      renderStoreList(stores);
      try {
        await ensureMap();
      } catch (err) {
        notify((err && err.message) || "地图加载失败", false);
      }
    });
  });
}

export async function loadOwnStoresPanel() {
  const hint = $("os-store-hint");
  const tk = state.me?.tenant_kind || "";
  if (hint) {
    hint.textContent =
      tk === "platform_mall"
        ? "登记连锁实体店：在地图点击选点，填写店名与电话即可；active 后自动建立自营经营关系。"
        : "当前接入类型非 platform_mall，门店登记仍可用，但商城自营链路以平台店为主。";
  }

  await loadProvinces();
  const { ok, data } = await api("GET", "/api/v1/fitting/mall/stores");
  const stores = ok && data ? data.items || data.stores || [] : [];
  if (!currentStoreId && stores[0]) writeStoreFields(stores[0]);
  else if (currentStoreId) {
    const hit = stores.find((s) => s.store_id === currentStoreId);
    if (hit) writeStoreFields(hit);
  }
  renderStoreList(stores);
  await ensureMap();
}

async function saveStore() {
  const shopName = ($("os-store-name") && $("os-store-name").value.trim()) || "";
  const contact = ($("os-store-contact") && $("os-store-contact").value.trim()) || "";
  const lng = readNum("os-store-lng");
  const lat = readNum("os-store-lat");
  if (!shopName) {
    notify("请填写店名", false);
    return;
  }
  // 联系电话：新建必填；修改时允许留空 —— dist 同步来的门店本来就没有电话，
  // 强制补电话会把「只改选址」卡死（2026-10-20 实测保存无请求的根因）。
  if (!contact && !currentStoreId) {
    notify("请填写联系电话", false);
    return;
  }
  if (lng == null || lat == null) {
    notify("请在地图上点击选择门店位置", false);
    return;
  }
  // shop_code 由系统生成、建档后固定：请求里不带，后端新建自动生成、修改保留原码。
  const body = {
    store_id: currentStoreId || undefined,
    shop_name: shopName,
    cs_contact: contact || undefined,
    lng,
    lat,
    address_text: ($("os-store-address") && $("os-store-address").value.trim()) || undefined,
    city_code: ($("os-store-city-code") && $("os-store-city-code").value.trim()) || undefined,
    city_name: ($("os-store-city-name") && $("os-store-city-name").value.trim()) || undefined,
    status: ($("os-store-status") && $("os-store-status").value) || "pending",
  };
  const { ok, data } = await api("PUT", "/api/v1/fitting/mall/stores", { body });
  if (!ok) {
    notify((data && data.message) || "保存失败", false);
    return;
  }
  writeStoreFields(data);
  notify("门店已保存", true);
  await loadOwnStoresPanel();
}

async function retireStore() {
  const sid = currentStoreId;
  if (!sid) {
    notify("请先选择要停用的门店", false);
    return;
  }
  if (!window.confirm("确定停用该门店？停用后 C 端不再展示，店铺码保留；可在状态下拉改回 pending/active。")) {
    return;
  }
  const { ok, data } = await api("DELETE", `/api/v1/fitting/mall/stores/${encodeURIComponent(sid)}`);
  if (!ok) {
    notify((data && data.message) || "停用失败", false);
    return;
  }
  notify("门店已停用", true);
  await loadOwnStoresPanel();
}

export function initOwnStoresHandlers() {
  bindClick("btn-os-store-new", async () => {
    clearStoreForm();
    document.querySelectorAll(".os-store-row-active").forEach((r) => r.classList.remove("os-store-row-active"));
    await ensureMap();
    notify("请在地图选点，填写店名与电话后保存", true);
  });
  bindClick("btn-os-store-save", () => saveStore());
  bindClick("btn-os-store-retire", () => retireStore());
  bindClick("btn-os-store-refresh", () => loadOwnStoresPanel());
  const province = $("os-region-province");
  const city = $("os-region-city");
  const district = $("os-region-district");
  if (province) province.addEventListener("change", () => onProvinceChange());
  if (city) city.addEventListener("change", () => onCityChange());
  if (district) district.addEventListener("change", () => onDistrictChange());
}
